import { ApiResponse, ERROR, Endpoint, EndpointError } from 'endpoints/endpoint';

const RESPONSE_TIMEOUT = 5 * 1000;
const MAX_ATTEMPTS = 3;
const noResponse = new Error('RESPONSE_TIMEOUT_ERROR');

export function apiRequest<Params extends Record<string, unknown> | void, Result, E extends EndpointError>(
  endpoint: Endpoint<Params, Result, E>,
  { retries = true, data, apiTarget }: { retries: boolean; data: Params; apiTarget?: 'client' | 'client-v2' },
) {
  let attempt = 0;

  return async function apiRequestAttempt(): Promise<ApiResponse<Result, E>> {
    let response;

    try {
      response = await Promise.race<ApiResponse<Result, E>>([
        endpoint(data).invoke(apiTarget ?? 'client'),
        new Promise((_, reject) =>
          setTimeout(() => reject({ success: false, details: noResponse.message }), RESPONSE_TIMEOUT),
        ),
      ]);

      if (response.success) {
        return response;
      }

      throw new Error(response.code || 'Error');
    } catch (error) {
      if (retries && attempt < MAX_ATTEMPTS - 1) {
        attempt += 1;

        return new Promise((resolve) => {
          setTimeout(() => resolve(apiRequestAttempt()), 1000);
        });
      }

      return (
        response ??
        ({ success: false, code: ERROR.UNEXPECTED_ERROR, details: (error as Error).message } as ApiResponse<Result, E>)
      );
    }
  };
}
